<template>
  <div class="chat-container">
    <i class="bi bi-x leave-icon colseChatRoom-Mobile" @click="toggleShowChatRoom" v-show="!showSuperChatArea && !showSticker && isMobile"></i>
    <ChatMessage :cid="cid" @toggle-showChatRoom="toggleShowChatRoom" ref="chatMessageComponent" />
    <div class="user-input">
      <div class="super-chat-area">
        <div v-if="!showSuperChatArea" class="super-chat-btn" @click="toggleSuperChat">
          <div class="aria-label" :aria-label="$t('streamingVideo.key004')">
            <img src="@/assets/img/member/program/super_chat.svg" :alt="$t('streamingVideo.key004')" class="chat-input-btn" />
          </div>
          <p>{{ $t('streamingVideo.key004') }}</p>
        </div>
        <SuperChatArea v-else @close-superChat="toggleSuperChat" @update-select-sc-id="updateSelectedSCId" ref="superChatArea" :scs="scs" :scInfo="scInfo" />
      </div>
      <div class="sticker-area" v-show="showSticker">
        <StickerArea v-if="stickers.length" :stickers="stickers" @sticker-selected="addStickerToInput" />
      </div>

      <div class="chat-input">
        <div class="input-container">
          <div
            contenteditable="true"
            ref="messageInput"
            :class="['message-input', { 'super-chat-open': showSuperChatArea }]"
            @input="updateMessageInput"
            @copy="handleCopy"
            @paste="handlePaste"
            @keydown.enter.prevent="handleEnter"
            @compositionstart="isComposing = true"
            @compositionend="isComposing = false"
          ></div>
          <div class="chat-input-bar-button">
            <div class="aria-label" :aria-label="$t('streamingVideo.key003')" @click="toggleSticker" v-show="isMobile">
              <img src="@/assets/img/streaming/streaming_StickerBtn_all_50x50.svg" :alt="$t('streamingVideo.key003')" class="chat-input-btn" />
            </div>
            <p class="remaining-chars" :class="{ exceeded: remainingChars < 0 }" v-show="remainingChars < 150">
              {{ remainingChars }}
            </p>
          </div>
        </div>
        <div class="msg-input-nav-bar" v-if="!showSuperChatArea">
          <div class="chat-input-bar-button">
            <div class="aria-label" :aria-label="$t('streamingVideo.key003')" @click="toggleSticker" v-show="!isMobile">
              <img src="@/assets/img/streaming/streaming_StickerBtn_all_50x50.svg" :alt="$t('streamingVideo.key003')" class="chat-input-btn" />
            </div>
            <div
              class="send-btn aria-label"
              :class="{ 'send-btn-disable': remainingChars < 0 || remainingChars === 200 }"
              :aria-label="$t('streamingVideo.key002')"
              @click="sendMessage()"
            >
              <img src="@/assets/img/streaming/streaming_sandMsgD_all_50x50.svg" :alt="$t('streamingVideo.key002')" class="chat-input-btn" />
            </div>
          </div>
        </div>
        <div class="msg-input-nav-bar" v-else>
          <div class="chat-input-bar-button" v-show="!isMobile">
            <div class="aria-label" :aria-label="$t('streamingVideo.key003')" @click="toggleSticker">
              <img src="@/assets/img/streaming/streaming_StickerBtn_all_50x50.svg" :alt="$t('streamingVideo.key003')" class="chat-input-btn" />
            </div>
          </div>
        </div>
      </div>
      <div class="input-alert">
        <p>{{ inputAlert }}</p>
      </div>
      <div
        class="super-chat-sand-btn"
        :class="{
          'send-btn-disable': remainingChars < 0 || remainingChars === 200 || (scInfo.find((sc) => sc.scId === selectSCId)?.sessionId == null && (scInfo.find((sc) => sc.scId === selectSCId)?.qty ?? 0) < 1),
        }"
        v-if="showSuperChatArea"
        @click="sendMessage(true)"
      >
        <p>{{ SCbuttonText }}</p>
        <img src="@/assets/img/member/program/send_super_chat.svg" alt="" class="chat-input-btn" />
      </div>
    </div>
    <PurchaseModal :visible="showPurchaseModal" :onClose="closePurchaseModal" :price="selectSCPrice" :orderId="orderId" @set-loading="setLoading" />
    <div v-if="showAgeCheckModal" class="custom-age-modal-overlay">
      <div class="custom-age-modal-content">
        <div class="custom-age-modal-message">
          <p>購買以上商品</p>
          <p>已通過法定代理人確認</p>
        </div>
        <div class="custom-age-modal-buttons">
          <button class="custom-age-btn custom-age-btn-cancel" @click="handleAgeCheckCancel">否</button>
          <button class="custom-age-btn custom-age-btn-confirm" @click="handleAgeCheckConfirm">是</button>
        </div>
      </div>
    </div>
    <Loading v-show="isLoading" />
  </div>
</template>

<script>
import { defineComponent, inject, onBeforeMount, computed } from 'vue';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import ChatMessage from '@/pages/programVideo/chatRoom/ChatMessage.vue';
import StickerArea from '@/pages/programVideo/chatRoom/Sticker.vue';
import SuperChatArea from '@/pages/programVideo/chatRoom/SuperChat.vue';
import forbiddenWords from '@/assets/data/forbiddenWords.json';
import { streamingPostMsg } from '@/api/streamingService.js';
import PurchaseModal from '@/components/PurchaseModal.vue';
import Loading from '@/components/Loading.vue';
import { HttpStatusCode } from 'axios';
import { getUserInfo } from '@/api/apiService';
import { createAuthOrder } from '@/api/cartService';
import { getSCInfo } from '@/api/streamingService.js';
import { useStore } from 'vuex';

export default defineComponent({
  name: 'ChatRoom',
  emits: ['toggle-showLoginModal', 'toggle-showChatRoom'],
  props: {
    cid: {
      type: String,
      required: true,
    },
    scs: {
      type: Array,
      default: () => [],
    },
    stickers: {
      type: Array,
      default: () => [],
    },
    getUserItems: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    StickerArea,
    SuperChatArea,
    ChatMessage,
    PurchaseModal,
    Loading,
  },
  setup() {
    const handleCompetedLogin = inject('handleCompetedLogin');
    const messageInput = ref(null);
    const scInfo = ref([]);
    const isLoading = ref(false);
    const showAgeCheckModal = ref(false);
    const ageCheckResolve = ref(null);

    const store = useStore();
    const currentLanguage = computed(() => store.state.currentLanguage);

    const handleOutsideClick = (event) => {
      if (messageInput.value && !messageInput.value.contains(event.target)) {
        messageInput.value.blur();
      }
    };
    const handlePaste = (event) => {
      event.preventDefault();
      const plainText = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, plainText);
    };

    onMounted(() => {
      document.addEventListener('click', handleOutsideClick);
    });

    onBeforeMount(async () => {
      await fetchSCInfo();
    });

    onBeforeUnmount(async () => {
      document.removeEventListener('click', handleOutsideClick);
    });

    const fetchSCInfo = async () => {
      try {
        isLoading.value = true;

        const langSetting = currentLanguage.value === 'zh_tw' ? 'zh-tw' : currentLanguage.value;
        const response = await getSCInfo(langSetting);
  
        scInfo.value = response.data.data;
      } catch (error) {
        console.error(error);
      } finally {
        isLoading.value = false;
      }
    }

    const showAgeCheckModalAsync = () => {
      showAgeCheckModal.value = true;
      return new Promise((resolve) => {
        ageCheckResolve.value = resolve;
      });
    }

    const handleAgeCheckConfirm = () => {
      showAgeCheckModal.value = false;
      if (ageCheckResolve.value) ageCheckResolve.value(true);
      ageCheckResolve.value = null;
    }

    const handleAgeCheckCancel = () => {
      showAgeCheckModal.value = false;
      if (ageCheckResolve.value) ageCheckResolve.value(false);
      ageCheckResolve.value = null;
    }

    return {
      handleOutsideClick,
      handlePaste,
      handleCompetedLogin,
      isLoading,
      scInfo,
      currentLanguage,
      showAgeCheckModal,
      showAgeCheckModalAsync,
      handleAgeCheckConfirm,
      handleAgeCheckCancel,
    };
  },
  data() {
    return {
      showSticker: false,
      showSuperChatArea: false,
      showLoginModal: true,
      messageInput: '',
      inputAlert: '',
      forbiddenWords: forbiddenWords,
      maxChars: 200,
      countChar: 0,
      selectSCId: null,
      selectSCPrice: null,
      orderId: null,
      isSending: false,
      isComposing: false,
      showPurchaseModal: false,
    };
  },
  computed: {
    remainingChars() {
      // Calculate sticker characters first
      const imgCount = (this.messageInput.match(/<img\b[^>]*>/gi) || []).length;
      // HTML Decoding
      const decodedMessage = this.decodeHtml(this.messageInput);
      const textLength = decodedMessage.replace(/<img\b[^>]*>/gi, '').length;
      const totalLength = textLength + imgCount * 4;
      return this.maxChars - totalLength;
    },
    isMobile() {
      return this.$store.state.isMobile;
    },
    jwt() {
      return this.$store.state.jwt;
    },
    SCbuttonText() {
      const selectedSC = this.scs.find((sc) => sc.id === this.selectSCId);
      return selectedSC && selectedSC.qty > 0 ? this.$t('streamingVideo.key006') : this.$t('streamingVideo.key027');
    },
  },
  methods: {
    handleEnter(event) {
      if (this.showSuperChatArea || this.isComposing) {
        return;
      }
      event.preventDefault();
      this.sendMessage();
    },
    setLoading(value) {
      this.isLoading = value;
    },
    async closePurchaseModal() {
      this.showPurchaseModal = false;
      this.orderId = null;
      try {
        this.isLoading = true;
        await this.getUserItems();

        const selectedSC = this.scs.find((sc) => sc.id === this.selectSCId);
        if (!selectedSC || selectedSC.qty <= 0) {
          return;
        }

        this.$gtm.push({
          event: 'sc_purchase_complete',
          sc_amount: this.selectSCPrice,
          device_type: navigator.userAgent,
          custom_page_path: this.cid,
          transaction_type: 'SuperChat',
          value: this.selectSCPrice,
        });

        this.sendMessage(true);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async startPurchaseSc() {
      try {
        if (!this.jwt) {
          return;
        }
        this.isLoading = true;
        const userInfo = await getUserInfo(this.jwt);

        // create order
        const scInfo = this.scInfo.find((sc) => sc.scId === this.selectSCId);
        if (!scInfo || !scInfo.sessionId || !scInfo.packId) {
          return;
        }
        let ageCheckSelection = 1;

        // Calculate age
        const birthDate = new Date(userInfo.birth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;

        if (age < 18) {
          // for not to block by loading
          this.isLoading = false;
          const result = await this.showAgeCheckModalAsync();
          if (!result) {
            return; // User cancelled
          }
          ageCheckSelection = 2;
          this.isLoading = true;
        }

        const createOrderData = {
          checkProduct: false,
          orderItems: [
            {
              sessionId: scInfo.sessionId,
              packId: scInfo.packId,
              quantity: 1,
            },
          ],
          ageCheckSelection,
          name: userInfo.name || '未填寫',
          phone: userInfo.phone,
          email: userInfo.contactEmail,
          address: userInfo.address,
          language: this.currentLanguage,
          shipping: 0,
          payment: ['CREDIT'],
          orderSource: 'Streaming',
          inframePayment: true,
        };

        const langSetting = this.currentLanguage === 'zh_tw' ? 'zh-tw' : this.currentLanguage;
        const response = await createAuthOrder(createOrderData, langSetting, this.jwt);

        if (response.data?.data) {
          this.orderId = response.data.data;

          this.showPurchaseModal = true;
        }
      } catch (error) {
        console.error('Error fetching start purchase SC:', error);
      } finally {
        this.isLoading = false;
      }
    },
    decodeHtml(input) {
      const parser = new DOMParser();
      const decodedString = parser.parseFromString(input, 'text/html').documentElement.textContent;
      return decodedString;
    },
    // sticker
    addStickerToInput(sticker) {
      const imgHtml = `<img src="${sticker.src}" alt="${sticker.alt}" class="input-sticker" />`;
      this.$refs.messageInput.innerHTML += imgHtml;
      this.updateMessageInput();
    },
    // message
    updateMessageInput() {
      this.messageInput = this.$refs.messageInput.innerHTML;
      // Exceeding -100: Remove excess characters or stickers.
      while (this.remainingChars < -100) {
        const lastImg = this.$refs.messageInput.querySelector('img:last-of-type');
        if (lastImg) {
          lastImg.remove();
        } else {
          this.messageInput = this.messageInput.slice(0, -1);
          this.$refs.messageInput.innerHTML = this.messageInput;
        }
      }

      const messageInputElement = this.$refs.messageInput;
      messageInputElement.scrollTop = messageInputElement.scrollHeight;
    },
    //Send Msg
    async sendMessage(SCSend = false) {
      if (SCSend && (this.remainingChars < 0 || this.remainingChars >= 200)) {
        this.$gtm.push({
          event: 'sc_inputEmpty_click',
          sc_amount: this.selectSCPrice,
          device_type: navigator.userAgent,
          custom_page_path: this.cid,
        });
        return;
      }

      if (!this.jwt) {
        this.$emit('toggle-showLoginModal');
        return;
      }
      if (this.isSending) return;
      this.isSending = true;
      let transformedMessage = this.transformMessageInput();

      // Check if the message contains only spaces and prevent sending
      if (/^\s*$/.test(transformedMessage)) {
        this.isSending = false;
        return;
      }
      // Remove trailing spaces from the message before sending
      transformedMessage = transformedMessage.trimEnd();

      if (!this.isWordingValid(transformedMessage)) {
        this.inputAlert = this.$t('member-information.key068');
        this.isSending = false;
        return;
      } else if (SCSend) {
        const selectedSC = this.scs.find((sc) => sc.id === this.selectSCId);
        if (!selectedSC || selectedSC.qty <= 0) {
          this.startPurchaseSc();
          this.isSending = false;
          this.$gtm.push({
            event: 'sc_purchase_attempt',
            sc_amount: this.selectSCPrice,
            device_type: navigator.userAgent,
            custom_page_path: this.cid,
            transaction_type: 'SuperChat',
            value: this.selectSCPrice,
          });
          return;
        }
      }

      this.inputAlert = '';

      const data = {
        msg: transformedMessage,
        sc: SCSend ? this.selectSCId || '' : '',
      };
      const cid = this.cid;

      try {
        await streamingPostMsg(cid, data, this.jwt);
        this.showSticker = false;
        this.showSuperChatArea = false;
        this.$refs.messageInput.innerHTML = '';
        this.messageInput = '';
        this.$refs.chatMessageComponent.resetChatUpdates();
        this.$refs.chatMessageComponent.scrollToBottom();

        if (SCSend) {
          this.$gtm.push({
            event: 'sc_send',
            sc_amount: this.selectSCPrice,
            device_type: navigator.userAgent,
            custom_page_path: this.cid,
            transaction_type: 'SuperChat',
            value: this.selectSCPrice,
          });
        } else {
          this.$gtm.push({
            event: 'send_msg',
            device_type: navigator.userAgent,
            custom_page_path: this.cid,
          });
        }

        this.getUserItems();
      } catch (error) {
        const statusCode = error.response?.status;
        if (statusCode === 422) {
          this.$refs.messageInput.innerHTML = '';
          this.messageInput = '';
          alert(this.$t('streamingVideo.key014'));

          this.getUserItems();
        } else if (statusCode === HttpStatusCode.Unauthorized && typeof this.handleCompetedLogin === 'function') {
          this.handleCompetedLogin();
        } else {
          console.error('Unexpected error:', error);
          alert(this.$t('streamingVideo.key015'));
        }
      } finally {
        this.isSending = false;
      }
    },
    //Convert img tag
    transformMessageInput() {
      const rawHTML = this.$refs.messageInput.innerHTML.trim();
      const replacedHTML = rawHTML.replace(/<img\s+[^>]*alt=["']([^"']+)_(\d+)["'][^>]*>/g, (_, id, index) => `<[emoji="${id}" index=${index}/emoji]>`);

      const parser = new DOMParser();
      const decodedHTML = parser.parseFromString(replacedHTML, 'text/html').documentElement.textContent;

      return decodedHTML;
    },
    toggleSticker() {
      if (!this.jwt) {
        this.$emit('toggle-showLoginModal');
        return;
      }

      if (this.isMobile) {
        this.showSuperChatArea = false;
      }
      this.showSticker = !this.showSticker;

      if (this.showSticker) {
        this.$gtm.push({
          event: 'sticker_button_click',
          custom_page_path: this.cid,
        });
      }

      this.$nextTick(() => {
        this.$refs.chatMessageComponent.scrollToBottom();
      });
    },
    toggleSuperChat() {
      if (!this.jwt) {
        this.$emit('toggle-showLoginModal');
        return;
      }

      if (this.isMobile) {
        this.showSticker = false;
      }
      this.showSuperChatArea = !this.showSuperChatArea;

      if (this.showSuperChatArea) {
        this.$gtm.push({
          event: 'sc_button_click',
          device_type: navigator.userAgent,
          custom_page_path: this.cid,
        });
      }

      this.$nextTick(() => {
        this.$refs.chatMessageComponent.scrollToBottom();
      });
    },
    updateSelectedSCId(newId, price) {
      this.selectSCId = newId;
      this.selectSCPrice = price;

      this.$gtm.push({
        event: 'sc_amount_adjust',
        device_type: navigator.userAgent,
        custom_page_path: this.cid,
      });
    },
    toggleShowChatRoom() {
      this.$emit('toggle-showChatRoom');
    },
    // check forbiddenWords
    isWordingValid(value) {
      const lowerCaseValue = value.toLowerCase();

      for (let word of this.forbiddenWords) {
        if (lowerCaseValue.includes(word)) {
          return false;
        }
      }
      return true;
    },
  },
});
</script>

<style lang="scss" scoped>
.custom-age-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.custom-age-modal-content {
  background: #111;
  border-radius: 8px;
  padding: 24px 32px 20px 32px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.4);
  min-width: 240px;
  text-align: center;
}
.custom-age-modal-message p {
  color: #fff;
  font-size: 18px;
  margin: 0 0 6px 0;
  font-weight: bold;
}
.custom-age-modal-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 18px;
}
.custom-age-btn {
  width: 60px;
  height: 40px;
  font-size: 18px;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
  margin: 0 8px;
  transition: background 0.2s;
}
.custom-age-btn-cancel {
  background: #ff7f2a;
}
.custom-age-btn-cancel:hover {
  background: #e65c00;
}
.custom-age-btn-confirm {
  background: #4caf50;
}
.custom-age-btn-confirm:hover {
  background: #388e3c;
}
</style>
